import type { LdLead, LdMetrics, LdSourceKey, LdStatusKey, LdTimelineEvent } from '@elva/ui';
import { growthGet, growthList, growthPost } from '@/data/http';
import { defaultMarketingPeriod } from '@/data/marketingScope';
import type { BeLead, BeLeadTimelineEvent, BeLeadsSummary } from '@/data/marketingApiTypes';
import { num, stableNumericId, str } from '@/data/adapt';
import { leadStatusToBe, leadStatusToFe } from '@/data/growthEnums';

export interface LeadsData {
  items: LdLead[];
  idMap: Record<number, string>;
  metrics: LdMetrics;
  campaignOptions: string[];
}

/** e.g. 2026-07-21T07:48:58.571638Z → "Jul 21, 7:48 AM" */
function formatLeadDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (!Number.isFinite(date.getTime())) return str(iso);
  const day = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${day}, ${time}`;
}

function centsToDollars(cents: number | null | undefined): number | null {
  if (cents == null || !Number.isFinite(cents)) return null;
  return Math.round(cents / 100);
}

function leadValueDollars(row: BeLead): number | null {
  if (row.revenue_cents != null) return centsToDollars(row.revenue_cents);
  if (row.value != null) return centsToDollars(row.value);
  return null;
}

function unwrapLeadRow(payload: BeLead | { item?: BeLead }): BeLead {
  if (payload && typeof payload === 'object' && 'item' in payload && payload.item) {
    return payload.item;
  }
  return payload as BeLead;
}

function adaptTimelineEvent(event: BeLeadTimelineEvent): LdTimelineEvent {
  return {
    at: formatLeadDate(event.at),
    label: str(event.label),
    ...(event.type ? { type: event.type } : {}),
  };
}

function adaptLead(row: BeLead): { lead: LdLead; backendId: string } {
  const campaign =
    typeof row.campaign === 'object'
      ? str((row.campaign as { name?: string })?.name)
      : str(row.campaign ?? row.campaign_name);
  const source = (row.source ?? 'call').toLowerCase() as LdSourceKey;
  const rawAppt = row.appt_date ?? row.appointment_date ?? null;

  return {
    backendId: row.id ?? '',
    lead: {
      id: stableNumericId(row.id ?? ''),
      name: str(row.name ?? row.patient_name),
      phone: str(row.phone),
      source: source === 'form' ? 'form' : 'call',
      campaign,
      keyword: str(row.keyword, '—'),
      date: formatLeadDate(row.date ?? row.created_at),
      duration: row.duration != null ? str(row.duration) : null,
      status: leadStatusToFe(row.status) as LdStatusKey,
      treatment: row.treatment ? str(row.treatment) : null,
      value: leadValueDollars(row),
      apptDate: rawAppt ? formatLeadDate(rawAppt) : null,
      callSummary: str(row.ai_call_summary ?? row.call_summary ?? row.summary, '') || null,
      recordingUrl: str(row.recording_url ?? row.call_recording_url, '') || null,
    },
  };
}

function sourceConversionPct(source?: {
  conversion_pct?: number;
  booking_rate?: number;
}): number {
  if (!source) return 0;
  if (source.conversion_pct != null) return Math.round(num(source.conversion_pct));
  if (source.booking_rate != null) {
    const rate = num(source.booking_rate);
    return rate <= 1 ? Math.round(rate * 100) : Math.round(rate);
  }
  return 0;
}

function adaptSummary(
  summary: BeLeadsSummary | undefined,
  items: LdLead[],
  totalCount?: number,
): LdMetrics {
  if (summary) {
    const kpis = summary.kpis;
    const total = num(kpis?.total ?? summary.total, totalCount ?? items.length);
    const booked = num(kpis?.booked ?? summary.booked);
    const treated = num(kpis?.treated ?? summary.treated);
    const noshow = num(kpis?.no_shows ?? summary.noshow ?? summary.no_show);
    const revenue =
      summary.revenue ??
      centsToDollars(summary.revenue_cents) ??
      centsToDollars(kpis?.revenue_cents) ??
      0;
    const calls = num(summary.sources?.call?.value ?? summary.sources?.call?.count ?? summary.calls);
    const forms = num(summary.sources?.form?.value ?? summary.sources?.form?.count ?? summary.forms);
    const callConv =
      summary.call_conv != null ? num(summary.call_conv) : sourceConversionPct(summary.sources?.call);
    const formConv =
      summary.form_conv != null ? num(summary.form_conv) : sourceConversionPct(summary.sources?.form);
    return {
      total,
      booked,
      treated,
      noshow,
      revenue,
      calls,
      forms,
      callConv,
      formConv,
    };
  }
  const total = totalCount ?? items.length;
  const booked = items.filter((l) => l.status === 'booked' || l.status === 'treated' || l.status === 'showed').length;
  const treated = items.filter((l) => l.status === 'treated').length;
  const noshow = items.filter((l) => l.status === 'noshow' || l.status === 'lost').length;
  const revenue = items.reduce((a, l) => a + (l.value ?? 0), 0);
  const calls = items.filter((l) => l.source === 'call').length;
  const forms = items.filter((l) => l.source === 'form').length;
  return {
    total,
    booked,
    treated,
    noshow,
    revenue,
    calls,
    forms,
    callConv: calls > 0 ? Math.round((booked / calls) * 100) : 0,
    formConv: forms > 0 ? Math.round((booked / forms) * 100) : 0,
  };
}

export async function fetchLeadTimeline(backendId: string): Promise<LdTimelineEvent[]> {
  const data = await growthGet<{ items?: BeLeadTimelineEvent[] } | BeLeadTimelineEvent[]>(
    `/leads/${encodeURIComponent(backendId)}/timeline/`,
  );
  const rows = Array.isArray(data) ? data : (data.items ?? []);
  return rows.map(adaptTimelineEvent);
}

export async function fetchLeads(options?: {
  page?: number;
  limit?: number;
  period?: string;
}): Promise<LeadsData> {
  const params: Record<string, string | number> = {
    page: options?.page ?? 1,
    limit: options?.limit ?? 100,
  };

  const { items: rows, meta } = await growthList<BeLead>('/leads/', params);

  const items: LdLead[] = [];
  const idMap: Record<number, string> = {};
  for (const row of rows) {
    const { lead, backendId } = adaptLead(row);
    items.push(lead);
    idMap[lead.id] = backendId;
  }
  const campaigns = [...new Set(items.map((l) => l.campaign).filter(Boolean))];

  const period = options?.period ?? defaultMarketingPeriod();
  const summary = await growthGet<BeLeadsSummary>('/leads/summary/', { period }).catch(() => undefined);

  return {
    items,
    idMap,
    metrics: summary ? adaptSummary(summary, items, meta?.total_count) : emptyLeadMetrics(meta?.total_count),
    campaignOptions: ['All campaigns', ...campaigns],
  };
}

function emptyLeadMetrics(totalCount?: number): LdMetrics {
  const total = totalCount ?? 0;
  return { total, booked: 0, treated: 0, noshow: 0, revenue: 0, calls: 0, forms: 0, callConv: 0, formConv: 0 };
}

export async function fetchLeadDetail(backendId: string, numericId: number): Promise<LdLead> {
  const [detailPayload, timeline] = await Promise.all([
    growthGet<BeLead | { item?: BeLead }>(`/leads/${encodeURIComponent(backendId)}/`),
    fetchLeadTimeline(backendId).catch(() => [] as LdTimelineEvent[]),
  ]);
  const row = unwrapLeadRow(detailPayload);
  const { lead } = adaptLead({ ...row, id: backendId });
  return {
    ...lead,
    id: numericId,
    ...(timeline.length ? { timeline } : {}),
  };
}

export async function updateLeadStatus(id: string, status: LdStatusKey, reason?: string) {
  return growthPost(`/leads/${id}/manual-status/`, {
    status: leadStatusToBe(status),
    reason,
  });
}

export async function recomputeLeadAttribution(id: string) {
  return growthPost(`/leads/${id}/recompute-attribution/`);
}
